import { Action, Reducer } from 'redux';
import { fork, call, takeEvery, put, select } from 'redux-saga/effects';
import Api from '../api';
import { IRootState } from './index';
import { actionCreators as layoutActionCreators } from '../reducers/Layout';
import { selectors as authSelectors } from '../reducers/Auth';
import PerformanceComment from 'src/models/PerformanceComment';

// Store
export interface IPerformanceCommentState {
    performanceComments: Array<PerformanceComment> | undefined;
}

const initialState: IPerformanceCommentState = {
    performanceComments: undefined,
};

// Action Types
export enum actionTypes {
    GET_PERFORMANCE_COMMENTS = '[performanceComment] GET_PERFORMANCE_COMMENTS',
    GET_PERFORMANCE_COMMENTS_SUCCESS = '[performanceComment] GET_PERFORMANCE_COMMENTS_SUCCESS',
    CREATE_PERFORMANCE_COMMENT = '[performanceComment] CREATE_PERFORMANCE_COMMENT',
    UPDATE_PERFORMANCE_COMMENT = '[performanceComment] UPDATE_PERFORMANCE_COMMENT',
    DELETE_PERFORMANCE_COMMENT = '[performanceComment] DELETE_PERFORMANCE_COMMENT',
}

// Action Creator
export const actionCreators = {
    getPerformanceComments: (userId: number) => {
        return { type: actionTypes.GET_PERFORMANCE_COMMENTS, payload: userId };
    },
    getPerformanceCommentsSuccess: (performanceComments: Array<PerformanceComment>) => {
        return { type: actionTypes.GET_PERFORMANCE_COMMENTS_SUCCESS, payload: performanceComments };
    },
    createPerformanceComment: (comment: PerformanceComment) => {
        return { type: actionTypes.CREATE_PERFORMANCE_COMMENT, payload: comment };
    },
    updatePerformanceComment: (comment: PerformanceComment) => {
        return { type: actionTypes.UPDATE_PERFORMANCE_COMMENT, payload: comment };
    },
    deletePerformanceComment: (comment: PerformanceComment) => {
        return { type: actionTypes.DELETE_PERFORMANCE_COMMENT, payload: comment };
    },
};

// Reducer
const reducer: Reducer<IPerformanceCommentState> = (state: IPerformanceCommentState = initialState, action: Action): IPerformanceCommentState => {
    switch (action.type) {
        case actionTypes.GET_PERFORMANCE_COMMENTS_SUCCESS: {
            const payload = (action as { type: string; payload: Array<PerformanceComment> }).payload;
            return {
                ...state,
                performanceComments: payload,
            };
        }
        default:
            return state;
    }
};

export default reducer;

// Selectors
const performanceCommentsSelector = (state: IRootState): Array<PerformanceComment> | undefined => state.performanceComment.performanceComments;

export const selectors = {
    performanceComments: performanceCommentsSelector,
};

// Side-Effects
const sagas = {
    getPerformanceComments: function* (action: { type: string; payload: number }) {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const userId = action.payload;
        const jwt = yield select(authSelectors.jwt);
        const result = yield call(Api.PerformanceComment.getComments, userId, jwt);
        yield put(actionCreators.getPerformanceCommentsSuccess(result));
    },
    createPerformanceComment: function* (action: { type: string; payload: PerformanceComment }) {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const comment = action.payload;
        const jwt = yield select(authSelectors.jwt);
        yield call(Api.PerformanceComment.createPerformanceComment, comment, jwt);
        yield put(layoutActionCreators.hideDialog());
        yield put(actionCreators.getPerformanceComments(comment.userId));
    },
    updatePerformanceComment: function* (action: { type: string; payload: PerformanceComment }) {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const comment = action.payload;
        const jwt = yield select(authSelectors.jwt);
        yield call(Api.PerformanceComment.updatePerformanceComment, comment, jwt);
        yield put(layoutActionCreators.hideDialog());
        yield put(actionCreators.getPerformanceComments(comment.userId));
    },
    deletePerformanceComment: function* (action: { type: string; payload: PerformanceComment }) {
        yield call(layoutActionCreators.waitIniitalLoadState);
        const comment = action.payload;
        const jwt = yield select(authSelectors.jwt);
        yield call(Api.PerformanceComment.deletePerformanceComment, comment, jwt);
        yield put(actionCreators.getPerformanceComments(comment.userId));
    },
};

const watches = {
    getPerformanceComments: function* () {
        yield takeEvery(actionTypes.GET_PERFORMANCE_COMMENTS, sagas.getPerformanceComments);
    },
    createPerformanceComment: function* () {
        yield takeEvery(actionTypes.CREATE_PERFORMANCE_COMMENT, sagas.createPerformanceComment);
    },
    updatePerformanceComment: function* () {
        yield takeEvery(actionTypes.UPDATE_PERFORMANCE_COMMENT, sagas.updatePerformanceComment);
    },
    deletePerformanceComment: function* () {
        yield takeEvery(actionTypes.DELETE_PERFORMANCE_COMMENT, sagas.deletePerformanceComment);
    },
};

export function* saga() {
    yield fork(watches.getPerformanceComments);
    yield fork(watches.createPerformanceComment);
    yield fork(watches.updatePerformanceComment);
    yield fork(watches.deletePerformanceComment);
}



// WEBPACK FOOTER //
// ./src/reducers/PerformanceComment.ts